import {ModaleLayout} from '@/components/Modale/ModaleLayout';
import {VStack} from '@chakra-ui/layout';
import {
  Box,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import {HaliRenderOne} from '@synako/haligator';
import {isItemStateCatalogueFF} from 'ff-common';
import {State} from '@synako/halitrek';
import {ItemCatalogue2Desc} from 'api-types/ffconnect-hal';
import {useTranslation} from 'react-i18next';
import {ArticleBlock} from '../panier/PanierFound/ArticleBlock';
import {dateAvecSlashes} from 'ff-common';
import {usePrixHistoriqueArticle} from './usePrixHistoriqueArticle';
import {
  HistoriqueAchatCategories,
  HistoriqueAchatPrix,
} from './HistoriqueAchatPrix';

export interface HistoriqueAchatModalProps {
  itemState: State<ItemCatalogue2Desc> | undefined;
}

export const HistoriqueAchatModal = NiceModal.create(
  ({itemState}: HistoriqueAchatModalProps) => {
    const {t} = useTranslation();
    const modal = NiceModal.useModal();
    // Charger les prix historiques de l'article
    const {peutVoirPrix, prixParDate} = usePrixHistoriqueArticle(
      itemState && isItemStateCatalogueFF(itemState)
        ? itemState.data.code
        : undefined,
      true,
    );

    if (!itemState || !isItemStateCatalogueFF(itemState)) return null;

    const code_article = itemState.data.code;
    const unite = itemState.data.unite_de_commande_client;
    const nombreDeColonnes = peutVoirPrix ? 4 : 2;
    const largeurColonne = peutVoirPrix ? '25%' : '50%';

    return (
      <ModaleLayout
        visible={modal.visible}
        hide={() => modal.hide()}
        titre={t('historique_livraison.title')}
        size_desktop={peutVoirPrix ? '3xl' : undefined}
      >
        <VStack
          align='start'
          mb={4}
          spacing={1}
        >
          <ArticleBlock code_article={code_article} />
        </VStack>

        <HaliRenderOne
          nav={itemState.follow('article_statistiques')}
          render={({data}) => {
            // Trier l'historique par date (du plus récent au plus ancien)
            const statsTriees = [...data.tolede.stats_par_livraison].sort(
              (a, b) =>
                new Date(b.livraison_du).getTime() -
                new Date(a.livraison_du).getTime(),
            );

            return (
              <TableContainer
                maxHeight='400px'
                overflowY='auto'
              >
                <Table
                  variant='unstyled'
                  size='md'
                  width='100%'
                >
                  {/* En-tête du tableau */}
                  <Thead>
                    <Tr
                      backgroundColor='gray.bg'
                      borderRadius='8px'
                    >
                      <Th
                        color='gray.600'
                        fontWeight='medium'
                        fontSize='xs'
                        py={2}
                        width={largeurColonne}
                      >
                        {t('global.date')}
                      </Th>
                      <Th
                        isNumeric
                        color='gray.600'
                        fontWeight='medium'
                        fontSize='xs'
                        py={2}
                        width={largeurColonne}
                      >
                        {t('global.quantite')}
                      </Th>
                      {peutVoirPrix && (
                        <>
                          <Th
                            isNumeric
                            color='gray.600'
                            fontWeight='medium'
                            fontSize='xs'
                            py={2}
                            width={largeurColonne}
                          >
                            {t('historique_livraison.prix')}
                          </Th>
                          <Th
                            color='gray.600'
                            fontWeight='medium'
                            fontSize='xs'
                            py={2}
                            width={largeurColonne}
                          >
                            {t('historique_livraison.categorie')}
                          </Th>
                        </>
                      )}
                    </Tr>
                  </Thead>

                  {/* Espacement entre l'en-tête et le corps */}
                  <Tbody>
                    <Tr height='10px'>
                      <Td
                        colSpan={nombreDeColonnes}
                        padding={0}
                      ></Td>
                    </Tr>

                    {statsTriees.map((stat, index) => (
                      <Tr
                        key={`${stat.livraison_du}-${index}`}
                        border='1px solid'
                        borderColor='gray.100'
                        fontSize='sm'
                      >
                        <Td
                          py={2.5}
                          width={largeurColonne}
                        >
                          {dateAvecSlashes(stat.livraison_du)}
                        </Td>
                        <Td
                          isNumeric
                          py={2.5}
                          width={largeurColonne}
                        >
                          {stat.quantite}{' '}
                          {t(`unite.${unite}`, {count: stat.quantite})}
                        </Td>
                        {peutVoirPrix && (
                          <>
                            <Td
                              isNumeric
                              py={2.5}
                              width={largeurColonne}
                            >
                              <HistoriqueAchatPrix
                                prix={
                                  prixParDate &&
                                  (prixParDate.get(stat.livraison_du) ?? [])
                                }
                                uniteDeCommande={unite}
                                uniteDeFacturation={
                                  itemState.data.unite_de_facturation
                                }
                              />
                            </Td>
                            <Td
                              py={2.5}
                              width={largeurColonne}
                            >
                              <HistoriqueAchatCategories
                                prix={
                                  prixParDate &&
                                  (prixParDate.get(stat.livraison_du) ?? [])
                                }
                              />
                            </Td>
                          </>
                        )}
                      </Tr>
                    ))}
                    {statsTriees.length === 0 && (
                      <Tr>
                        <Td
                          colSpan={nombreDeColonnes}
                          textAlign='center'
                        >
                          {t('historique_livraison.aucun_historique')}
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            );
          }}
          skeleton={() => (
            <Box
              display='flex'
              justifyContent='center'
              py={6}
            >
              <Spinner size='lg' />
            </Box>
          )}
          fallback={() => (
            <Box
              textAlign='center'
              py={4}
            >
              <Text>{t('historique_livraison.aucun_historique')}</Text>
            </Box>
          )}
        />
      </ModaleLayout>
    );
  },
);
